import {
  type ClientResponse,
  type EventDetail,
  type EventsClient,
  type EventsErrorResponse,
  type EventsListResponse,
  type ListEventsParams,
  createEventsClient,
  normalizeEventsServiceUrl,
} from "@project-x/api-clients";
import { appLogger } from "@/lib/observability/logger";

const isServerSide = (): boolean => typeof window === "undefined";

/**
 * buildEventsFallbackUrl derives api.{host} from PUBLIC_HOST or the browser hostname.
 * Computed per call so a wrong bake-time NEXT_PUBLIC_EVENTS_URL cannot permanently
 * pin the client to production when the env var is empty at runtime.
 */
const buildEventsFallbackUrl = (): string => {
  const scheme = (process.env.NEXT_PUBLIC_PUBLIC_SCHEME ?? "https").trim();
  const host = (
    process.env.NEXT_PUBLIC_PUBLIC_HOST ??
    (typeof window !== "undefined" ? window.location.hostname : "gruv.fm")
  ).trim();
  return `${scheme}://api.${host}`;
};

/**
 * resolveEventsServiceUrl picks the events API base for the current runtime.
 * Server uses EVENTS_URL (in-cluster). Browser prefers NEXT_PUBLIC_EVENTS_URL,
 * then a host-derived fallback (api.{PUBLIC_HOST|window.hostname}) — never a
 * hardcoded production host that would break DEV builds with wrong bake-time env.
 */
export const resolveEventsServiceUrl = (baseUrl?: string): string => {
  if (isServerSide() && !baseUrl) {
    const serverUrl = process.env.EVENTS_URL;
    if (serverUrl) {
      return normalizeEventsServiceUrl(serverUrl);
    }
  }

  const clientUrl = process.env.NEXT_PUBLIC_EVENTS_URL?.trim();
  const resolved = baseUrl ?? (clientUrl && clientUrl.length > 0 ? clientUrl : buildEventsFallbackUrl());
  return normalizeEventsServiceUrl(resolved);
};

export const createConfiguredEventsClient = (overrides?: {
  baseUrl?: string;
  fetch?: typeof fetch;
}): EventsClient =>
  createEventsClient({
    baseUrl: resolveEventsServiceUrl(overrides?.baseUrl),
    fetch: overrides?.fetch,
    logger: appLogger,
  });

const defaultEventsClient = createConfiguredEventsClient();

export const fetchPublicEvents = (
  params?: ListEventsParams,
  client: EventsClient = defaultEventsClient
): Promise<ClientResponse<EventsListResponse, EventsErrorResponse>> => client.listEvents(params);

export const fetchEventDetail = (
  id: string,
  client: EventsClient = defaultEventsClient
): Promise<ClientResponse<EventDetail, EventsErrorResponse>> => client.getEvent({ id });

/**
 * Server-side cached version of fetchEventDetail.
 * Caches successful responses for 30s to reduce load on the events service
 * for high-traffic public event pages.
 */
export const fetchEventDetailCached = async (
  id: string,
  client: EventsClient = defaultEventsClient
): Promise<ClientResponse<EventDetail, EventsErrorResponse>> => {
  // Dynamic import to keep cache module server-only
  const { cached } = await import("@/lib/cache/server-cache");
  return cached(
    `event:${id}`,
    () => client.getEvent({ id }),
    30_000 // 30 seconds
  );
};
